import { useI18n } from '@ecom/i18n';
import { useId, useRef, type ReactNode } from 'react';

import { cx } from '../utils/cx';

export interface TabItem {
  readonly id: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly disabled?: boolean;
}

export interface TabsProps {
  /** Accessible name for the tab list, e.g. "Sections de la boutique". */
  readonly label: string;
  readonly tabs: readonly TabItem[];
  readonly activeId: string;
  readonly onChange: (id: string) => void;
  /** Panel content for the active tab. */
  readonly children: ReactNode;
}

/**
 * Tabs (§7.1 primitives), styled from the dashboard prototype's `.tab-btn`:
 * `--ecom-ink-soft` label, `--ecom-surface-hover` on hover, and the active
 * tab filled with `--ecom-accent-soft` on `--ecom-accent-ink`.
 *
 * Implements the ARIA tabs pattern with roving tabindex — only the active tab
 * is in the tab order, and Arrow keys move between tabs — rather than leaving
 * every tab tabbable. With ten tabs the latter forces ten Tab presses to
 * reach the panel.
 *
 * The direction detail worth stating: `ArrowRight` does not mean "next". In
 * Arabic the tab strip runs right-to-left, so `ArrowRight` must move to the
 * *previous* tab, matching what the user sees. The arrow-to-index mapping is
 * therefore resolved against the active locale's direction rather than
 * hardcoded, which is the sort of thing that silently reverses under RTL if
 * it is written once for LTR and never revisited.
 */
export function Tabs({ label, tabs, activeId, onChange, children }: TabsProps): ReactNode {
  const { direction } = useI18n();
  const baseId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const selectableIndexes = tabs
    .map((tab, index) => (tab.disabled === true ? -1 : index))
    .filter((index) => index !== -1);

  function moveFocus(targetIndex: number): void {
    const target = tabs[targetIndex];
    if (target === undefined) return;

    onChange(target.id);
    listRef.current
      ?.querySelector<HTMLButtonElement>(`#${CSS.escape(tabIdFor(baseId, target.id))}`)
      ?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    const currentPosition = selectableIndexes.indexOf(tabs.findIndex((tab) => tab.id === activeId));
    if (currentPosition === -1) return;

    // In RTL the visual order of the strip is reversed, so the arrow that
    // points "forward" on screen is the opposite physical key.
    const forwardKey = direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const backwardKey = direction === 'rtl' ? 'ArrowRight' : 'ArrowLeft';

    let nextPosition: number | undefined;

    if (event.key === forwardKey) {
      nextPosition = (currentPosition + 1) % selectableIndexes.length;
    } else if (event.key === backwardKey) {
      nextPosition = (currentPosition - 1 + selectableIndexes.length) % selectableIndexes.length;
    } else if (event.key === 'Home') {
      nextPosition = 0;
    } else if (event.key === 'End') {
      nextPosition = selectableIndexes.length - 1;
    }

    if (nextPosition === undefined) return;

    event.preventDefault();
    const targetIndex = selectableIndexes[nextPosition];
    if (targetIndex !== undefined) moveFocus(targetIndex);
  }

  const activeTab = tabs.find((tab) => tab.id === activeId);

  return (
    <div className="ecom-tabs">
      <div
        ref={listRef}
        className="ecom-tabs__list"
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;

          return (
            <button
              key={tab.id}
              id={tabIdFor(baseId, tab.id)}
              type="button"
              role="tab"
              className={cx('ecom-tabs__tab', isActive && 'is-active')}
              aria-selected={isActive}
              aria-controls={panelIdFor(baseId, tab.id)}
              tabIndex={isActive ? 0 : -1}
              disabled={tab.disabled === true}
              onClick={() => {
                onChange(tab.id);
              }}
            >
              {tab.icon !== undefined ? (
                <span className="ecom-tabs__icon" aria-hidden="true">
                  {tab.icon}
                </span>
              ) : null}
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab !== undefined ? (
        <div
          id={panelIdFor(baseId, activeTab.id)}
          className="ecom-tabs__panel"
          role="tabpanel"
          aria-labelledby={tabIdFor(baseId, activeTab.id)}
          // Focusable so that a panel with no interactive content of its own
          // is still reachable when tabbing out of the tab strip.
          tabIndex={0}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

function tabIdFor(baseId: string, tabId: string): string {
  return `${baseId}-tab-${tabId}`;
}

function panelIdFor(baseId: string, tabId: string): string {
  return `${baseId}-panel-${tabId}`;
}
